<template>
  <div
    v-if="open && rect"
    class="spotlight"
    data-test="clara-spotlight"
  >
    <!--
      O escurecimento é feito com QUATRO faixas ao redor do alvo, e não com um
      overlay furado por box-shadow: a sombra não recebe clique, então o overlay
      inteiro deixaria passar o ponteiro. Com as faixas, o fundo bloqueia e o
      buraco fica realmente vazio — o clique cai no botão de verdade, que é o
      que o guia está pedindo para o usuário fazer.
    -->
    <div
      class="spotlight__scrim"
      :style="{ top: 0, left: 0, right: 0, height: px(hole.top) }"
      @click="finish"
    ></div>
    <div
      class="spotlight__scrim"
      :style="{ top: px(hole.bottom), left: 0, right: 0, bottom: 0 }"
      @click="finish"
    ></div>
    <div
      class="spotlight__scrim"
      :style="{ top: px(hole.top), left: 0, width: px(hole.left), height: px(hole.height) }"
      @click="finish"
    ></div>
    <div
      class="spotlight__scrim"
      :style="{ top: px(hole.top), left: px(hole.right), right: 0, height: px(hole.height) }"
      @click="finish"
    ></div>

    <div
      class="spotlight__ring"
      :style="ringStyle"
    ></div>

    <div
      class="spotlight__callout"
      :style="calloutStyle"
      role="dialog"
      aria-labelledby="clara-spotlight-title"
    >
      <span
        class="spotlight__arrow"
        :style="{ left: px(arrowOffset) }"
      ></span>

      <div class="spotlight__callout-head">
        <span class="spotlight__step">{{ $t('clara.spotlight.step') }}</span>
        <v-btn
          icon
          x-small
          class="spotlight__close"
          :aria-label="$t('clara.spotlight.closeLabel')"
          data-test="clara-spotlight-close"
          @click="finish"
        >
          <PhX :size="14" />
        </v-btn>
      </div>

      <h2
        id="clara-spotlight-title"
        class="spotlight__title"
      >
        {{ $t('clara.spotlight.title') }}
      </h2>
      <p class="spotlight__text">
        {{ $t('clara.spotlight.text') }}
      </p>

      <div class="spotlight__actions">
        <v-btn
          text
          small
          data-test="clara-spotlight-finish"
          @click="finish"
        >
          {{ $t('clara.spotlight.finishBtn') }}
        </v-btn>
        <v-btn
          depressed
          small
          class="spotlight__cta"
          data-test="clara-spotlight-open"
          @click="openClara"
        >
          {{ $t('clara.spotlight.openBtn') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { OPTIONS_STORE_CLARA } from '@/vuex/module/clara';

/** Respiro entre o alvo e o recorte claro, para o botão não encostar na borda. */
const HOLE_PADDING = 6;
const CALLOUT_WIDTH = 320;
const CALLOUT_GAP = 14;
const VIEWPORT_MARGIN = 16;

export default {
  name: 'ClaraIntroSpotlight',

  props: {
    open: {
      type: Boolean,
      default: false
    },
    target: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      rect: null,
      /* A largura da janela vive no data, e não lida direto de window: computed
         não observa `window.innerWidth`, então o balão ficava com a posição da
         medida anterior depois de um resize. */
      viewportWidth: 0
    };
  },

  computed: {
    hole() {
      const { top, left, width, height } = this.rect;

      return {
        top: top - HOLE_PADDING,
        left: left - HOLE_PADDING,
        right: left + width + HOLE_PADDING,
        bottom: top + height + HOLE_PADDING,
        width: width + HOLE_PADDING * 2,
        height: height + HOLE_PADDING * 2
      };
    },

    ringStyle() {
      return {
        top: this.px(this.hole.top),
        left: this.px(this.hole.left),
        width: this.px(this.hole.width),
        height: this.px(this.hole.height)
      };
    },

    calloutWidth() {
      return Math.min(CALLOUT_WIDTH, this.viewportWidth - VIEWPORT_MARGIN * 2);
    },

    /* O balão nasce centrado no alvo e é empurrado para dentro da viewport. O
       alvo fica no canto superior direito, então sem essa correção metade do
       texto cairia fora da tela. */
    calloutLeft() {
      const centered = this.hole.left + this.hole.width / 2 - this.calloutWidth / 2;
      const max = this.viewportWidth - this.calloutWidth - VIEWPORT_MARGIN;

      return Math.max(VIEWPORT_MARGIN, Math.min(centered, max));
    },

    calloutStyle() {
      return {
        top: this.px(this.hole.bottom + CALLOUT_GAP),
        left: this.px(this.calloutLeft),
        width: this.px(this.calloutWidth)
      };
    },

    arrowOffset() {
      const center = this.hole.left + this.hole.width / 2 - this.calloutLeft;

      return Math.max(20, Math.min(center, this.calloutWidth - 20));
    }
  },

  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (value) {
          this.start();

          return;
        }

        this.stop();
      }
    }
  },

  beforeDestroy() {
    this.stop();
  },

  methods: {
    px(value) {
      return `${Math.round(value)}px`;
    },

    start() {
      this.measure();

      if (!this.rect) {
        this.$emit('finish');

        return;
      }

      window.addEventListener('resize', this.measure);
      window.addEventListener('scroll', this.measure, true);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('click', this.handleTargetClick, true);
    },

    stop() {
      window.removeEventListener('resize', this.measure);
      window.removeEventListener('scroll', this.measure, true);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleTargetClick, true);
    },

    measure() {
      this.viewportWidth = window.innerWidth;

      const element = document.querySelector(this.target);

      if (!element) {
        this.rect = null;

        return;
      }

      const { top, left, width, height } = element.getBoundingClientRect();

      this.rect = { top, left, width, height };
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.finish();
      }
    },

    /* O buraco deixa o clique chegar ao botão real. Quando isso acontece, o
       guia cumpriu o papel e sai de cena — sem fechar o painel que acabou de
       abrir. */
    handleTargetClick(event) {
      const element = document.querySelector(this.target);

      if (element && element.contains(event.target)) {
        this.finish();
      }
    },

    openClara() {
      this.$store.commit(OPTIONS_STORE_CLARA.MUTATIONS.SET_OPEN, true);
      this.finish();
    },

    finish() {
      this.$emit('finish');
    }
  }
};
</script>

<style scoped>
.spotlight {
  position: fixed;
  inset: 0;
  z-index: 300;
  pointer-events: none;
}

.spotlight__scrim {
  position: fixed;
  background: rgba(12, 24, 40, 0.62);
  pointer-events: auto;
  animation: spotlight-fade 0.2s ease;
}

.spotlight__ring {
  position: fixed;
  border-radius: var(--tc-radius-sm);
  border: 2px solid var(--clara-brand-soft);
  box-shadow: 0 0 0 4px var(--clara-glow);
  pointer-events: none;
  animation: spotlight-pulse 2s ease-in-out infinite;
}

.spotlight__callout {
  position: fixed;
  padding: var(--tc-space-4);
  border-radius: var(--tc-radius-overlay);
  background: var(--tc-surface, #fff);
  color: var(--tc-text, inherit);
  box-shadow: 0 18px 48px rgba(12, 24, 40, 0.32);
  pointer-events: auto;
  animation: spotlight-fade 0.2s ease;
}

.spotlight__arrow {
  position: absolute;
  top: -7px;
  width: 14px;
  height: 14px;
  margin-left: -7px;
  border-radius: 2px;
  background: var(--tc-surface, #fff);
  transform: rotate(45deg);
}

.spotlight__callout-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--tc-space-2);
}

.spotlight__step {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--clara-text);
}

.spotlight__close {
  margin-right: -4px;
}

.spotlight__title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 var(--tc-space-2);
}

.spotlight__text {
  font-size: 0.875rem;
  line-height: 1.55;
  margin: 0;
}

.spotlight__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--tc-space-2);
  margin-top: var(--tc-space-4);
}

.spotlight__cta {
  background: var(--clara-gradient) !important;
  color: var(--clara-on-gradient) !important;
  font-weight: 600;
}

@keyframes spotlight-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes spotlight-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px var(--clara-glow);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(227, 195, 144, 0.12);
  }
}

/* Quem pediu menos movimento fica só com o destaque estático. */
@media (prefers-reduced-motion: reduce) {
  .spotlight__ring {
    animation: none;
  }
}
</style>
